import { useEffect, useRef } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';

interface UnsavedChangesGuardOptions {
  message?: string;
  /** Browser Back leaves the full-page builder for Campaigns; an embedded builder keeps history. */
  redirectBackToCampaigns?: boolean;
}

export function useUnsavedChangesGuard(
  isDirty: boolean,
  {
    message = 'You have unsaved changes. Leave anyway?',
    redirectBackToCampaigns = true,
  }: UnsavedChangesGuardOptions = {},
) {
  const navigate = useNavigate();
  const pendingAction = useRef<'POP' | 'PUSH' | 'REPLACE' | null>(null);
  const bypassNextNavigation = useRef(false);
  // Guard 1: browser tab close / refresh
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, message]);

  // Guard 2: in-app navigation (requires data router — confirmed createBrowserRouter is used)
  const blocker = useBlocker(({ currentLocation, nextLocation, historyAction }) => {
    if (bypassNextNavigation.current) {
      bypassNextNavigation.current = false;
      return false;
    }
    const current = `${currentLocation.pathname}${currentLocation.search}${currentLocation.hash}`;
    const next = `${nextLocation.pathname}${nextLocation.search}${nextLocation.hash}`;
    const shouldBlock = current !== next;
    // The old builder always consumed browser Back and sent the user to the
    // campaigns list, even when the form was clean. Block POP long enough to
    // perform that redirect without showing the dirty-form confirmation.
    if (shouldBlock && historyAction === 'POP' && redirectBackToCampaigns) {
      pendingAction.current = historyAction;
      return true;
    }
    if (!isDirty) return false;
    if (shouldBlock) pendingAction.current = historyAction;
    return shouldBlock;
  });

  useEffect(() => {
    if (isDirty || blocker.state !== 'blocked' || pendingAction.current !== 'POP' || !redirectBackToCampaigns) return;
    pendingAction.current = null;
    bypassNextNavigation.current = true;
    blocker.reset();
    navigate('/campaigns');
  }, [blocker, isDirty, navigate, redirectBackToCampaigns]);

  const confirm = () => {
    if (blocker.state !== 'blocked') return;
    if (pendingAction.current === 'POP' && redirectBackToCampaigns) {
      // Match the old builder: browser Back exits to Campaigns rather than to
      // whatever history entry happened to precede the builder.
      pendingAction.current = null;
      bypassNextNavigation.current = true;
      blocker.reset();
      navigate('/campaigns');
      return;
    }
    pendingAction.current = null;
    blocker.proceed();
  };

  const cancel = () => {
    if (blocker.state !== 'blocked') return;
    pendingAction.current = null;
    blocker.reset();
  };

  return {
    open: isDirty && blocker.state === 'blocked',
    message,
    confirm,
    cancel,
  };
}
